import { MdCheck, MdLock } from 'react-icons/md';
import { Cohort, PresetAction } from '../types';
import { getCohortLockState, getTime } from '../utils';

// Steps whose edit actions are gated by the cohort lock. Kept in sync with
// the popup-level enforcement in the main AI Intros page.
const LOCKABLE_STEP_KEYS = new Set<string>(['selectParticipants', 'createMatches']);

interface ActionKeyConfig {
  popupName: string;
  actionName: string;
  displayTitle: string;
  comingSoon?: boolean;
}

// Map preset action keys to popup names and action names
const actionKeyConfig: { [key: string]: ActionKeyConfig } = {
  selectParticipants: { popupName: 'selectParticipants', actionName: 'selectParticipants', displayTitle: 'Select Participants' },
  welcomePackage: { popupName: 'onboarding', actionName: 'welcomePackage', displayTitle: 'Welcome Package' },
  connectionEmail: { popupName: 'launch', actionName: 'sendMatches', displayTitle: 'Launch 🚀' },
  introduceAndConnect: { popupName: 'introduceAndConnect', actionName: 'sendIntroConsent', displayTitle: 'Introduce & Connect' },
  feedbackRequest: { popupName: 'collectFeedback', actionName: 'collectFeedback', displayTitle: 'Collect Feedback' },
  createMatches: { popupName: 'createMatches', actionName: 'createMatches', displayTitle: 'Create Matches' },
  followUpEmail: { popupName: 'followUp', actionName: 'followUp', displayTitle: 'Follow-up Email', comingSoon: true },
  checkInEmail: { popupName: 'checkIn', actionName: 'sendCheckIn', displayTitle: 'Check-in Email' },
  successTips: { popupName: 'successTips', actionName: 'successTips', displayTitle: 'Success Tips', comingSoon: true },
  midProgramSurvey: { popupName: 'midSurvey', actionName: 'midSurvey', displayTitle: 'Mid-Program Survey', comingSoon: true },
  celebrationEmail: { popupName: 'celebration', actionName: 'celebration', displayTitle: 'Celebration Email', comingSoon: true },
  resourcesEmail: { popupName: 'resources', actionName: 'resources', displayTitle: 'Resources Email', comingSoon: true },
  finalThankYou: { popupName: 'thankYou', actionName: 'thankYou', displayTitle: 'Final Thank You', comingSoon: true },
};

// Default steps for cohorts without presetActions (standard workflow)
const defaultSteps: PresetAction[] = [
  { key: 'selectParticipants', title: 'Select Participants', description: '', daysAfterPrevious: 0, enabled: true },
  { key: 'createMatches', title: 'Create Matches', description: '', daysAfterPrevious: 0, enabled: true },
  { key: 'welcomePackage', title: 'Welcome Package', description: '', daysAfterPrevious: 0, enabled: true },
  { key: 'connectionEmail', title: 'Connection Email', description: '', daysAfterPrevious: 0, enabled: true },
  { key: 'checkInEmail', title: 'Check-in Email', description: '', daysAfterPrevious: 0, enabled: true },
  { key: 'feedbackRequest', title: 'Feedback Request', description: '', daysAfterPrevious: 0, enabled: true },
];

interface CohortRoadmapProps {
  cohort: Cohort;
  onOpenStep: (stepName: string) => void;
}

const CohortRoadmap = ({ cohort, onOpenStep }: CohortRoadmapProps) => {
  const actions = cohort.actions || [];
  const presetActions = cohort.presetActions;
  const lockState = getCohortLockState(cohort);

  // Get the most recent action entry for a given actionName
  const getLatestAction = (actionName: string) => {
    const matching = actions.filter(a => a.action === actionName);
    if (matching.length === 0) return null;
    return matching.sort((a, b) => getTime(b.createdAt) - getTime(a.createdAt))[0];
  };

  const getActionStatus = (presetKey: string, actionName: string): 'completed' | 'current' | 'upcoming' => {
    const presetAction = presetActions?.find(p => p.key === presetKey);
    const latest = getLatestAction(actionName);

    // If the latest action is terminated, the preset may be stale — treat as upcoming
    if (latest?.status === 'terminated') return 'upcoming';

    // If the latest action is completed, trust that over a stale preset
    if (latest?.status === 'completed') return 'completed';

    // Otherwise use presetActions
    if (presetAction?.status) {
      if (presetAction.status === 'completed') return 'completed';
      if (presetAction.status === 'active') return 'current';
      if (presetAction.status === 'pending') return 'upcoming';
    }

    // Fallback for legacy cohorts without presetActions
    if (latest?.status === 'active') return 'current';
    return 'upcoming';
  };

  // Use presetActions if available, otherwise use default steps
  const workflowSteps = presetActions && presetActions.length > 0 ? presetActions.filter(a => a.enabled) : defaultSteps;

  // Calculate completion
  const totalSteps = workflowSteps.length;
  const completedSteps = workflowSteps.filter(step => {
    const config = actionKeyConfig[step.key];
    return config && getActionStatus(step.key, config.actionName) === 'completed';
  }).length;

  return (
    <div className="mt-3 px-4 py-4 bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
      {/* Progress Summary */}
      <div className="mb-4">
        <h3 className="text-lg font-bold text-navy-700 dark:text-white mb-1">
          Cohort Progress
        </h3>
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {completedSteps}/{totalSteps} COMPLETED
        </p>
      </div>

      {/* Segmented Progress Bar with Steps Below */}
      <div className="flex gap-2">
        {workflowSteps.map((step, index) => {
          const config = actionKeyConfig[step.key] || {
            popupName: step.key,
            actionName: step.key,
            displayTitle: step.title,
            comingSoon: true
          };
          const status = getActionStatus(step.key, config.actionName);

          return (
            <div key={step.key} className={`flex-1 flex flex-col gap-2 ${config.comingSoon ? 'opacity-60' : ''}`}>
              <div className={`h-2 rounded-full transition-all overflow-hidden ${
                config.comingSoon
                  ? 'bg-brand-300 dark:bg-brand-400'
                  : status === 'completed'
                    ? 'bg-brand-500'
                    : 'bg-gray-200 dark:bg-gray-700'
              }`}>
                {status === 'current' && !config.comingSoon && (
                  <div className="h-full w-1/2 bg-brand-500 dark:bg-brand-400 animate-pulse"></div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-xs transition-all ${
                  config.comingSoon
                    ? 'border-2 border-gray-300 dark:border-gray-600 text-gray-400'
                    : status === 'completed'
                      ? 'bg-brand-500 text-white'
                      : status === 'current'
                        ? 'bg-brand-500 text-white ring-2 ring-brand-500/30'
                        : 'border-2 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400'
                }`}>
                  {!config.comingSoon && status === 'completed' ? (
                    <MdCheck className="h-3 w-3" />
                  ) : (
                    index + 1
                  )}
                </div>
                <div className="flex-1 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <div className={`text-xs font-medium ${
                      config.comingSoon
                        ? 'text-gray-500 dark:text-gray-400'
                        : status === 'current'
                          ? 'text-brand-600 dark:text-brand-400'
                          : 'text-navy-700 dark:text-white'
                    }`}>
                      {config.displayTitle}
                    </div>
                    {lockState.locked && LOCKABLE_STEP_KEYS.has(step.key) && !config.comingSoon && (
                      <span
                        className="inline-flex"
                        title={
                          lockState.reason === 'scheduled'
                            ? 'Locked - a send is scheduled'
                            : 'Locked - emails sent'
                        }
                        aria-label={
                          lockState.reason === 'scheduled'
                            ? 'Locked - a send is scheduled'
                            : 'Locked - emails sent'
                        }
                      >
                        <MdLock
                          className={`h-3 w-3 flex-shrink-0 ${
                            lockState.reason === 'scheduled'
                              ? 'text-amber-500 dark:text-amber-400'
                              : 'text-gray-500 dark:text-gray-400'
                          }`}
                        />
                      </span>
                    )}
                    {config.comingSoon && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 font-medium">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => onOpenStep(config.popupName)}
                    className="px-2 py-1 rounded bg-brand-50 dark:bg-brand-900/20 text-brand-500 hover:bg-brand-100 dark:hover:bg-brand-900/40 hover:text-brand-600 dark:hover:text-brand-400 transition-all text-[10px] font-medium"
                  >
                    {status === 'completed' || (lockState.locked && LOCKABLE_STEP_KEYS.has(step.key)) ? 'View' : 'Edit'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CohortRoadmap;
